import React, { useEffect, useState } from "react";
import { api, downloadPost } from "./api";
import { eastern } from "./RecordScreen";

/**
 * #21, the parent end-of-week report (build plan §11.1 #2): one per student.
 * A System Admin drafts them into the #17 queue (every student once the
 * session has ended, or one student at any time); an RN or Supervisor
 * releases each there. Print one, or all of them.
 */

interface Line {
  personKey: string;
  lastName: string;
  firstName: string;
  city: string;
  departed: boolean;
  adultSelf: boolean;
  notificationKey: string;
  status: string;
  toName: string;
  toContact: string;
  sentAtUtc: string;
}
interface Session { start: string; end: string; ended: boolean; endedBy: "date" | "action" | null; endedUtc: string }
interface View { lines: Line[]; session: Session; canDraft: boolean; canRelease: boolean }

/** What a long batch is doing, and for how long: the server answers once, at the end (≈ a minute for a full roster). */
export function Working({ what, startedMs }: { what: string; startedMs: number }): React.ReactElement {
  const [now, setNow] = useState(performance.now());
  useEffect(() => {
    const t = setInterval(() => setNow(performance.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const sec = Math.max(0, Math.floor((now - startedMs) / 1000));
  return (
    <p className="flex items-center gap-2 text-sm text-slate-700" role="status" aria-live="polite">
      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-program" aria-hidden="true" />
      <span>{what}… {Math.floor(sec / 60)}:{String(sec % 60).padStart(2, "0")}. A full roster takes a minute or two; keep this page open.</span>
    </p>
  );
}

const statusOf = (l: Line) => (!l.notificationKey ? "Not drafted" : l.status === "Sent" ? `Sent ${eastern(l.sentAtUtc)}` : l.status === "Draft" || l.status === "Approved" ? "Drafted: to release in #17" : l.status);

export function EndOfWeek(): React.ReactElement {
  const [v, setV] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** The batch running now (Draft all, Print all): what it says, and when it started. */
  const [working, setWorking] = useState<{ what: string; startedMs: number } | null>(null);
  const load = () => void api<View>("GET", "/api/eow").then((r) => (r.ok ? setV(r.data) : setError(r.data.error ?? "Could not load.")));
  useEffect(load, []);
  async function draft(personKey?: string) {
    setError(null);
    setNote(null);
    setBusy(personKey ?? "all");
    if (!personKey && v) {
      const n = v.lines.filter((l) => !l.notificationKey).length;
      setWorking({ what: `Drafting ${n} end-of-week report${n === 1 ? "" : "s"}`, startedMs: performance.now() });
    }
    const r = await api<{ result: { drafted: string[]; skipped: { personKey: string }[] }; lines: Line[] }>("POST", "/api/eow/draft", personKey ? { personKey } : {});
    setBusy(null);
    setWorking(null);
    if (!r.ok) return setError(r.data.error ?? "Not drafted.");
    setV((x) => (x ? { ...x, lines: r.data.lines } : x));
    const n = r.data.result.drafted.length;
    const k = r.data.result.skipped.length;
    setNote(`Drafted ${n} report${n === 1 ? "" : "s"}${k ? `; ${k} already drafted` : ""}. Release them in #17 Parent notifications.`);
  }
  async function print(personKey?: string) {
    setError(null);
    setBusy(`print-${personKey ?? "all"}`);
    if (!personKey && v) setWorking({ what: `Preparing the PDF of all ${v.lines.length} reports`, startedMs: performance.now() });
    const e = await downloadPost(personKey ? `/api/print/eow/${encodeURIComponent(personKey)}` : "/api/print/eow", "end-of-week.pdf");
    setBusy(null);
    setWorking(null);
    if (e) setError(e);
  }
  const s = v?.session;
  const drafted = v ? v.lines.filter((l) => l.notificationKey).length : 0;
  return (
    <section>
      <p className="mb-3 text-sm"><a href="/reports" className="text-program underline">← Reports</a> · <a href="/reports/17" className="text-program underline">#17 Parent notifications</a></p>
      <h2 className="mb-1 text-xl font-semibold">#21 Parent end-of-week reports</h2>
      <p className="mb-3 text-sm text-slate-600">One per student: the medications given (held, refused and not needed shown as such), the OTC items with the dosing authority, and the visits with when a parent was told. Voided visits are left out. An 18-or-older attendee who hasn't agreed to parent notification gets their own.</p>
      {error && <p className="field-error" role="alert">{error}</p>}
      {v && s && (
        <>
          <div className="section-card space-y-2">
            <p className="text-sm">{s.ended ? (s.endedBy === "action" ? `The session was ended on Config, ${eastern(s.endedUtc)}.` : `The session ended on ${s.end}.`) : `The session hasn't ended yet${s.end ? ` (last day ${s.end})` : ""}: draft one student at a time for an early departure.`}</p>
            <p className="text-sm text-slate-600" role="status">{drafted} of {v.lines.length} drafted.</p>
            <div className="flex flex-wrap gap-2">
              {v.canDraft && <button type="button" className="btn-primary min-h-11" disabled={!s.ended || busy !== null} onClick={() => void draft()}>{busy === "all" ? "Drafting…" : "Draft the end-of-week reports"}</button>}
              <button type="button" className="btn-secondary min-h-11" disabled={busy !== null} onClick={() => void print()}>{busy === "print-all" ? "Preparing…" : "Print all"}</button>
            </div>
            {working && <Working what={working.what} startedMs={working.startedMs} />}
            {note && <p className="text-sm text-green-700" role="status">{note}</p>}
          </div>
          <div className="section-card">
            <ul className="divide-y divide-slate-100">
              {v.lines.map((l) => (
                <li key={l.personKey} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span>
                    <a className="font-medium text-program underline" href={`/review/${encodeURIComponent(l.personKey)}`}>{l.lastName}, {l.firstName}</a>
                    <span className="text-slate-600"> · {l.city || "Not assigned"}</span>
                    {l.departed && <span className="ml-2 rounded bg-amber-200 px-1.5 text-xs font-semibold text-amber-950">left early</span>}
                    {l.adultSelf && <span className="ml-2 rounded bg-purple-100 px-1.5 text-xs font-semibold text-purple-900">AdultSelf</span>}
                    <span className="block text-xs text-slate-500">{statusOf(l)}{l.toName ? ` · to ${l.toName}${l.toContact ? ` (${l.toContact})` : ""}` : ""}</span>
                  </span>
                  <span className="flex gap-2">
                    {v.canDraft && !l.notificationKey && <button type="button" className="btn-secondary min-h-11" disabled={busy !== null} onClick={() => void draft(l.personKey)}>Draft</button>}
                    <button type="button" className="btn-secondary min-h-11" disabled={busy !== null} onClick={() => void print(l.personKey)}>Print</button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </section>
  );
}
